/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FC, useEffect, useRef, useState } from 'react';
import IconSettings from '@salesforce/design-system-react/components/icon-settings';
import Modal from '@salesforce/design-system-react/components/modal';
import Spinner from '@salesforce/design-system-react/components/spinner';
import { useLazyQuery, useMutation, useQuery } from '@apollo/react-hooks';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { GET_SALES_REPS } from '../../../../graphql/getSalesReps';
import { GET_PRIMARY_ACCOUNT } from '../../../../graphql/getPrimaryAccount';
import { GET_SHIP_TO_ADDRESSES } from '../../../../graphql/getShipToAddresses';
import { GET_FORM_CONFIGURATIONS } from '../../../../graphql/getFormConfigurations';
import CREATE_EXPENSE_INVENTORY from '../../../../graphql/mutations/createExpenseInventory';
import { ShipToAddress } from '../../../Shared/FormEngine/FormEngine.types';
import useSnackBar from '../../../../util/customHooks/useSnackBar';
import SnackBar from '../../../Shared/SnackBar';
import CancelEventModal from '../../../CreateEditSurgeryEvent/CancelEventCreationModal';
import { getUserInfo } from '../../../../store/ducks/userInfo';
import {
  NOTIFY_TYPE,
  PERSONAS_SALES_RESP,
  PERSONAS_ASSOCIATE_SALES_REP,
} from '../../../../util/constants';
import ExpenseInventoryFormModal from './ExpenseInventoryFormModal';
import { getWriteOffFieldConfig, parseExpenseInventoryFormConfig } from './expenseInventoryConfig';

const SHIPPING = 'Shipping';
const IH_PREFIX = 'IH';
const CREATION_FAILED_MESSAGE = 'Creation failed';
const UPDATE_SUCCESS_MESSAGE = 'Success: Expense Inventory successfully updated.';
const CREATE_SUCCESS_MESSAGE = 'Success: Expense Inventory successfully created.';
const GENERIC_ERROR_MESSAGE = 'Something went wrong. Please try again.';
const EDIT_MODAL_HEADING = 'Edit Expense Inventory';
const CREATE_MODAL_HEADING = 'Expense Inventory';

// `usageDate` is stored/returned as a date-only string (e.g. "2026-10-08"),
// which `new Date(...)` parses as UTC midnight. In timezones behind UTC that
// then renders as the previous local day. Shift by the timezone offset so the
// calendar date displayed matches the date shown on the detail header.
const parseUsageDate = (date?: string): Date | null => {
  if (!date) return null;
  const parsedDate = new Date(date);
  if (Number.isNaN(parsedDate.getTime())) return null;
  return new Date(parsedDate.getTime() + parsedDate.getTimezoneOffset() * 60000);
};

interface ExpenseInventoryEditDetail {
  id?: string;
  caseExternalId?: string;
  salesRepId?: string;
  salesRepName?: string;
  accountId?: string;
  accountDisplay?: string;
  shipTo?: string;
  shipToDisplay?: string;
  writeOffType?: string;
  usageDate?: string;
  usedInPatientCare?: boolean;
  marketingCollaboration?: boolean;
  doctorName?: string;
  facilityNameOrLocation?: string;
}

interface Props {
  isOpen: boolean;
  toggleOpen: () => void;
  refetch?: () => void;
  expenseInventoryDetail?: ExpenseInventoryEditDetail;
}

const ExpenseInventoryForm: FC<Props> = ({ isOpen, toggleOpen, refetch, expenseInventoryDetail }) => {
  const { open, notification, openSnackBar } = useSnackBar();
  const userInfo = useSelector(getUserInfo);
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const isEditMode = !!expenseInventoryDetail;

  console.log('ExpenseInventoryForm userInfo:', userInfo);

  // ── Dropdown selected values ───────────────────────────────────────────
  // When editing an existing case, pre-seed the selections directly from the
  // detail's ids/display names. The pickers accept a `{ id, label }` stub
  // without needing the matching option to have loaded in `items` yet.
  const [selectedSalesRep, setSelectedSalesRep] = useState<any>(() =>
    expenseInventoryDetail?.salesRepId
      ? { id: expenseInventoryDetail.salesRepId, label: expenseInventoryDetail.salesRepName }
      : null
  );
  const [selectedAccount, setSelectedAccount] = useState<any>(() =>
    expenseInventoryDetail?.accountId
      ? { id: expenseInventoryDetail.accountId, label: expenseInventoryDetail.accountDisplay }
      : null
  );
  const [selectedShipTo, setSelectedShipTo] = useState<any>(() =>
    expenseInventoryDetail?.shipTo
      ? { id: expenseInventoryDetail.shipTo, label: expenseInventoryDetail.shipToDisplay }
      : null
  );
  const [writeOffType, setWriteOffType] = useState<string>(
    expenseInventoryDetail?.writeOffType ?? ''
  );
  console.log('ExpenseInventoryForm selected values:', {
    selectedSalesRep,
    selectedAccount,
    selectedShipTo,
    writeOffType,
  });

  // Suppresses the single-option auto-select for Account once the user has
  // explicitly cleared it, so they can pick the value manually. Reset when
  // the Sales Rep changes (a new Account list is being fetched).
  const [accountAutoSelectSuppressed, setAccountAutoSelectSuppressed] = useState(false);
  // Same suppression behavior for Ship To, reset when Account changes.
  const [shipToAutoSelectSuppressed, setShipToAutoSelectSuppressed] = useState(false);

  // ── Cancel confirmation state ──────────────────────────────────────────
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  // ── Sales Rep (eager) ──────────────────────────────────────────────────
  const { data: salesRepsData } = useQuery(GET_SALES_REPS, {
    fetchPolicy: 'cache-first',
  });
  const salesReps = salesRepsData?.getSalesReps ?? [];

  const isSalesRepUser =
    userInfo?.personas === PERSONAS_SALES_RESP ||
    userInfo?.personas === PERSONAS_ASSOCIATE_SALES_REP;
  // Whether there is a currently active Sales Rep context to fetch
  // Account/Repstock Account data for. Used to avoid showing stale
  // lazy-query results after the Sales Rep selection is cleared.
  const hasActiveSalesRep = isSalesRepUser || !!selectedSalesRep;

  // ── Account (lazy — fires when Sales Rep selected) ───────────
  const [getPrimaryAccount, { data: primaryAccountData }] = useLazyQuery(GET_PRIMARY_ACCOUNT, {
    fetchPolicy: 'no-cache',
  });
  // Tracks which Sales Rep the latest-requested/resolved `primaryAccountData`
  // actually belongs to, so a response still in flight for a *previous*
  // Sales Rep can't be used to compute `accounts` (and incorrectly
  // auto-select an account from the wrong Sales Rep) while the request for
  // the current Sales Rep is still pending.
  const requestedAccountsSalesRepIdRef = useRef<string | null>(null);
  const [accountsSalesRepId, setAccountsSalesRepId] = useState<string | null>(null);
  useEffect(() => {
    if (primaryAccountData) {
      setAccountsSalesRepId(requestedAccountsSalesRepIdRef.current);
    }
  }, [primaryAccountData]);
  const activeSalesRepId = isSalesRepUser ? userInfo?.id : selectedSalesRep?.id;
  const accounts =
    hasActiveSalesRep && accountsSalesRepId === activeSalesRepId
      ? (primaryAccountData?.getPrimaryAccount ?? []).filter((account: any) =>
          account.number?.startsWith(IH_PREFIX)
        )
      : [];
  console.log('ExpenseInventoryForm accounts:', accounts, primaryAccountData?.getPrimaryAccount);

  // ── Ship To (lazy — fires when Account selected) ─────────────
  const [getShipToAddresses, { data: shipToData }] = useLazyQuery(GET_SHIP_TO_ADDRESSES, {
    fetchPolicy: 'no-cache',
  });
  // Same stale-data guard as `accountsSalesRepId` above, but keyed by Account
  // id instead of Sales Rep id.
  const requestedShipToAccountIdRef = useRef<string | null>(null);
  const [shipToAccountId, setShipToAccountId] = useState<string | null>(null);
  useEffect(() => {
    if (shipToData) {
      setShipToAccountId(requestedShipToAccountIdRef.current);
    }
  }, [shipToData]);
  const shipToAddresses =
    selectedAccount && shipToAccountId === selectedAccount?.id
      ? (shipToData?.getShipToAddresses ?? []).filter((address: ShipToAddress) =>
          address.name?.startsWith(IH_PREFIX)
        )
      : [];

  // ── Business Config (for write-off field visibility / required rules) ──
  const { data: configData } = useQuery(GET_FORM_CONFIGURATIONS, {
    fetchPolicy: 'cache-first',
  });

  console.log('ExpenseInventoryForm ++++++++ configData:', configData?.getFormConfigurations);
  const expenseInventoryConfig = parseExpenseInventoryFormConfig(
    (configData?.getFormConfigurations as any)?.expenseInventory ?? null
  );
  const fieldConfig = getWriteOffFieldConfig(expenseInventoryConfig, writeOffType);

  console.log('ExpenseInventoryForm ++++++++ expenseInventoryConfig:', expenseInventoryConfig);
  console.log('ExpenseInventoryForm ++++++++ fieldConfig:', fieldConfig);
  console.log('ExpenseInventoryForm ++++++++ writeOffType:', writeOffType);

  // ── Auto-select Sales Rep for Sales Rep / Associate / Branch Op users ──
  useEffect(() => {

    const salesRepId = isSalesRepUser ? userInfo?.id : selectedSalesRep?.id;
    console.log('ExpenseInventoryForm useEffect 74 salesRepId:', salesRepId);
    if (!salesRepId) return;

    requestedAccountsSalesRepIdRef.current = salesRepId;
    getPrimaryAccount({ variables: { salesRepId } });
  }, [selectedSalesRep, userInfo, isSalesRepUser, getPrimaryAccount]);

  // ── Reset child dropdowns when Sales Rep changes ───────────────────────
  const handleSalesRepChange = (value: any): void => {
    setSelectedSalesRep(value);
    setSelectedAccount(null);
    setSelectedShipTo(null);
    setAccountAutoSelectSuppressed(false);
  };

  // ── Fetch Ship To when Account changes ────────────────────────
  const handleAccountChange = (value: any): void => {
    setSelectedAccount(value);
    setSelectedShipTo(null);
    setAccountAutoSelectSuppressed(!value);
    setShipToAutoSelectSuppressed(false);
    if (value?.id) {
      requestedShipToAccountIdRef.current = value.id;
      getShipToAddresses({ variables: { accountId: value.id, type: SHIPPING } });
    }
  };

  // ── Default Account when there is exactly one option ─────────
  useEffect(() => {
    if (!selectedAccount && accounts.length === 1 && !accountAutoSelectSuppressed) {
      handleAccountChange(accounts[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts, selectedAccount, accountAutoSelectSuppressed]);

  // ── Default Ship To when there is exactly one option ──────────
  const handleShipToChange = (value: ShipToAddress | null): void => {
    setSelectedShipTo(value);
    setShipToAutoSelectSuppressed(!value);
  };

  useEffect(() => {
    if (!selectedShipTo && shipToAddresses.length === 1 && !shipToAutoSelectSuppressed) {
      handleShipToChange(shipToAddresses[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipToAddresses, selectedShipTo, shipToAutoSelectSuppressed]);

  // ── Edit mode: seed Ship To options for the case's existing account ────
  // Normal Account selection changes fetch Ship To via handleAccountChange,
  // but the edit-mode Account is pre-seeded directly into state above, so that
  // handler never runs for it. Fetch its Ship To options once on mount instead.
  useEffect(() => {
    if (expenseInventoryDetail?.accountId) {
      requestedShipToAccountIdRef.current = expenseInventoryDetail.accountId;
      getShipToAddresses({
        variables: { accountId: expenseInventoryDetail.accountId, type: SHIPPING },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Mutations ──────────────────────────────────────────────────────────
  const [createExpenseInventory, { loading: savingExpenseInventory }] = useMutation(
    CREATE_EXPENSE_INVENTORY
  );

  // ── Submit orchestration ───────────────────────────────────────────────
  const handleSave = async (formValues: any): Promise<void> => {
    setLoading(true);
    try {
      const payload = {
        caseId: expenseInventoryDetail?.id,
        caseExternalId: expenseInventoryDetail?.caseExternalId,
        salesRepId: selectedSalesRep?.id ?? userInfo?.id,
        writeOffType,
        accountId: selectedAccount?.id ?? null,
        shipToId: selectedShipTo?.id ?? null,
        usageDate: formValues.usageDate ?? null,
        usedInPatientCare: formValues.usedInPatientCare ?? null,
        marketingCollaboration: formValues.marketingCollaboration ?? null,
        doctorName: formValues.doctorName ?? null,
        facilityNameOrLocation: formValues.facilityNameOrLocation ?? null,
      };

      console.log('ExpenseInventoryForm handleSave ************ payload:', payload);

      const step1 = await createExpenseInventory({ variables: payload });
      const step1Result = step1?.data?.createExpenseInventory;

      if (step1Result?.message !== 'success') {
        throw new Error(step1Result?.message ?? CREATION_FAILED_MESSAGE);
      }

      setLoading(false);
      openSnackBar(
        NOTIFY_TYPE.SUCCESS,
        isEditMode
          ? UPDATE_SUCCESS_MESSAGE
          : CREATE_SUCCESS_MESSAGE
      );
      setTimeout(() => {
        toggleOpen();
        if (isEditMode) {
          if (refetch) refetch();
        } else {
          // Redirect to the newly created case's detail view rather than just
          // refreshing the list. `message === 'success'` (checked above) confirms
          // the case was created and the mutation returned its externalId, so
          // it's available for the detail query to look up.
          history.push('/inventoryExpenseDetails', { externalId: step1Result.externalId });
        }
      }, 2000);
    } catch (err) {
      setLoading(false);
      const message = (err as any)?.message ?? GENERIC_ERROR_MESSAGE;
      openSnackBar(NOTIFY_TYPE.ERROR, message);
    }
  };

  // ── Cancel handling ────────────────────────────────────────────────────
  const handleCancelRequest = (): void => {
    setShowCancelConfirm(true);
  };

  const handleCancelConfirm = (): void => {
    setShowCancelConfirm(false);
    toggleOpen();
  };

  const handleCancelDismiss = (): void => {
    setShowCancelConfirm(false);
  };

  const handleTypeChange = (value: string): void => {
    console.log('ExpenseInventoryForm handleTypeChange value:', value);
    setWriteOffType(value);
  };

  const modalHeading = isEditMode ? EDIT_MODAL_HEADING : CREATE_MODAL_HEADING;

  return (
    <IconSettings iconPath="/icons">
      <Modal
        isOpen={isOpen}
        className="form-engine-modal"
        onRequestClose={loading ? undefined : handleCancelRequest}
        ariaHideApp={false}
        heading={<div className="slds-float_left slds-text-title_bold">{modalHeading}</div>}
        dismissOnClickOutside={false}
      >
        {(loading || savingExpenseInventory) && (
          <Spinner size="small" variant="brand" assistiveText={{ label: 'Saving...' }} />
        )}
        <section className="slds-p-around_medium slds-theme_shade" key="form">
          <ExpenseInventoryFormModal
            writeOffType={writeOffType}
            onWriteOffTypeChange={handleTypeChange}
            fieldConfig={fieldConfig}
            salesReps={salesReps}
            selectedSalesRep={selectedSalesRep}
            onSalesRepChange={handleSalesRepChange}
            accounts={accounts}
            selectedAccount={selectedAccount}
            onAccountChange={handleAccountChange}
            shipToAddresses={shipToAddresses}
            selectedShipTo={selectedShipTo}
            onShipToChange={handleShipToChange}
            isSaving={loading || savingExpenseInventory}
            onCancel={handleCancelRequest}
            onSubmit={handleSave}
            initialValues={{
              usageDate: parseUsageDate(expenseInventoryDetail?.usageDate),
              usedInPatientCare: expenseInventoryDetail?.usedInPatientCare ?? false,
              marketingCollaboration: expenseInventoryDetail?.marketingCollaboration ?? false,
              doctorName: expenseInventoryDetail?.doctorName ?? '',
              facilityNameOrLocation: expenseInventoryDetail?.facilityNameOrLocation ?? '',
            }}
          />
        </section>
      </Modal>
      <CancelEventModal
        isNew
        canceModalVisible={showCancelConfirm}
        handleCancelModal={handleCancelDismiss}
        handleCancelModalConfirmPress={handleCancelConfirm}
      />
      <SnackBar open={open} notification={notification} />
    </IconSettings>
  );
};

export default ExpenseInventoryForm;
